import { useState } from "react";
import { Check, X, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AdminGarageTabs } from "@/components/layout/AdminGarageTabs";
import { PageHeader, LoadingState, ErrorState, EmptyState, Badge, ConfirmDialog, Field } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

const changed = () => window.dispatchEvent(new Event("gf:signup-requests"));

function RejectDialog({ row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/signup-requests/${row.garage_id}/reject`, { reason });
      toast.success(t("p7.approval.rejectedOk"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reject-request-dialog">
        <DialogHeader><DialogTitle>{t("p7.approval.rejectTitle", { name: row?.garage_name })}</DialogTitle><DialogDescription>{t("p7.approval.rejectText")}</DialogDescription></DialogHeader>
        <Field label={t("p7.approval.reason")} htmlFor="rj-reason"><Textarea id="rj-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" /></Field>
        <Button variant="destructive" disabled={busy} onClick={submit} data-testid="reject-confirm-button">{t("p7.approval.reject")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function RequestRow({ r, onApprove, onReject }) {
  return (
    <div className="gf-card flex flex-wrap items-start gap-4 p-5" data-testid={`signup-request-row-${r.slug}`}>
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg font-bold text-slate-900">{r.garage_name} <span className="text-sm font-normal text-slate-500">/g/{r.slug}</span></p>
        <p className="mt-1 text-sm text-slate-700">{r.manager?.name} · {r.manager?.email}{r.manager?.phone ? ` · ${r.manager.phone}` : ""}</p>
        <p className="mt-1 text-xs text-slate-500">{r.plan} · {t(`p7.pricing.${r.interval || "monthly"}`)} · {fmtDateTime(r.created_at)}</p>
        {r.status === "rejected" && r.reason && <p className="mt-2 text-xs text-red-700">{t("p7.approval.reason")}: {r.reason}</p>}
      </div>
      <Badge tone={r.status === "pending" ? "amber" : "red"} testId={`signup-request-status-${r.slug}`}>{t(`p7.approval.status.${r.status}`)}</Badge>
      {r.status === "pending" && (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onApprove(r)} data-testid={`approve-request-${r.slug}`}><Check className="mr-1.5 h-4 w-4" />{t("p7.approval.approve")}</Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => onReject(r)} data-testid={`reject-request-${r.slug}`}><X className="mr-1.5 h-4 w-4" />{t("p7.approval.reject")}</Button>
        </div>
      )}
    </div>
  );
}

export default function AdminSignupRequests() {
  const { data, error, loading, reload } = useFetch("/admin/signup-requests");
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const done = () => { setApproving(null); setRejecting(null); reload(); changed(); };
  const approve = async () => {
    try {
      const { data: r } = await api.post(`/admin/signup-requests/${approving.garage_id}/approve`);
      toast.success(t("p7.approval.approvedOk", { n: r.trial_days }));
      done();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <AdminGarageTabs />
      <PageHeader eyebrow={t("admin.area")} title={t("p7.approval.nav")} subtitle={t("p7.approval.subtitle")} />
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState icon={UserPlus} title={t("p7.approval.empty")} />}
      <div className="space-y-3" data-testid="signup-requests-list">
        {data?.map((r) => <RequestRow key={r.garage_id} r={r} onApprove={setApproving} onReject={setRejecting} />)}
      </div>
      <ConfirmDialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)} title={t("p7.approval.approveTitle", { name: approving?.garage_name })}
        description={t("p7.approval.approveText")} confirmLabel={t("p7.approval.approve")} onConfirm={approve} testId="approve-request-dialog" />
      <RejectDialog row={rejecting} onClose={() => setRejecting(null)} onDone={done} />
    </div>
  );
}
